/**
 * Widget Registry
 *
 * Central registry for all CoPilot Guide widgets/plugins.
 * Handles registration and retrieval of widget components.
 */

import type { WidgetDefinition, WidgetDisplayMode } from '../types/guide';

export class WidgetRegistry {
  private widgets: Map<string, WidgetDefinition> = new Map();
  private aliases: Map<string, string> = new Map();

  /**
   * Register a widget
   */
  public register(definition: WidgetDefinition): void {
    this.widgets.set(definition.name.toLowerCase(), definition);

    if (definition.aliases) {
      definition.aliases.forEach((alias) => {
        this.aliases.set(alias.toLowerCase(), definition.name.toLowerCase());
      });
    }
  }

  /**
   * Register multiple widgets at once
   */
  public registerAll(definitions: WidgetDefinition[]): void {
    definitions.forEach((def) => this.register(def));
  }

  /**
   * Get widget by name (supports aliases)
   */
  public get(name: string): WidgetDefinition | undefined {
    const normalizedName = name.toLowerCase();

    // Check if it's an alias
    const canonicalName = this.aliases.get(normalizedName) || normalizedName;

    return this.widgets.get(canonicalName);
  }

  /**
   * Get widget component by name
   */
  public getComponent(name: string): WidgetDefinition['component'] | undefined {
    return this.get(name)?.component;
  }

  /**
   * Check if widget exists (supports aliases)
   */
  public has(name: string): boolean {
    const normalizedName = name.toLowerCase();
    const canonicalName = this.aliases.get(normalizedName) || normalizedName;
    return this.widgets.has(canonicalName);
  }

  /**
   * Get all registered widget names (canonical names only)
   */
  public getAllNames(): string[] {
    return Array.from(this.widgets.keys());
  }

  /**
   * Get all registered widgets
   */
  public getAll(): WidgetDefinition[] {
    return Array.from(this.widgets.values());
  }

  /**
   * Get display mode for a widget
   */
  public getDisplayMode(widgetName: string, requestedMode?: WidgetDisplayMode): WidgetDisplayMode {
    const widget = this.get(widgetName);
    if (!widget) return 'modal';

    if (requestedMode && widget.displayModes.includes(requestedMode)) {
      return requestedMode;
    }

    return widget.defaultDisplayMode;
  }

  /**
   * Check if widget supports a display mode
   */
  public supportsDisplayMode(widgetName: string, mode: WidgetDisplayMode): boolean {
    const widget = this.get(widgetName);
    if (!widget) return false;
    return widget.displayModes.includes(mode);
  }

  /**
   * Unregister a widget
   */
  public unregister(name: string): boolean {
    const normalizedName = name.toLowerCase();
    const widget = this.widgets.get(normalizedName);
    if (!widget) return false;

    if (widget.aliases) {
      widget.aliases.forEach((alias) => {
        this.aliases.delete(alias.toLowerCase());
      });
    }

    return this.widgets.delete(normalizedName);
  }

  /**
   * Clear all registered widgets
   */
  public clear(): void {
    this.widgets.clear();
    this.aliases.clear();
  }

  /**
   * Get widget count
   */
  public get size(): number {
    return this.widgets.size;
  }

  /**
   * Validate persisted widget state on restore.
   *
   * When a widget bumps `stateVersion` or its `stateSchema` rejects the
   * persisted shape (renamed/removed fields, garbage from a stale
   * tenant), we drop the state instead of crashing the widget on mount.
   * The caller hands us whatever came back from the server; we hand
   * back either the original (if valid) or `undefined` (drop and seed
   * with defaults).
   *
   * Returns `{ state, dropped, reason? }` so the caller can log
   * structured drift events to telemetry without inferring why.
   */
  public sanitizeState(
    widgetName: string,
    persisted: unknown
  ): {
    state: Record<string, unknown> | undefined;
    dropped: boolean;
    reason?: string;
  } {
    if (persisted === undefined || persisted === null) {
      return { state: undefined, dropped: false };
    }

    if (typeof persisted !== 'object') {
      return { state: undefined, dropped: true, reason: 'not_an_object' };
    }

    const obj = persisted as Record<string, unknown>;
    const widget = this.get(widgetName);

    if (!widget) {
      // Unknown widget — KEEP the state. Companion sub-widgets and
      // internal modals (e.g. `language_test_modal`) legitimately share
      // the persistence channel without being top-level registry
      // entries; dropping their state on every mount left users
      // re-doing work they had already completed. Genuine renames are
      // handled by `stateVersion` checks below, which still apply when
      // a widget IS registered. Pre-2026-04-28 this returned `dropped:
      // true, reason: 'unknown_widget'` and silently wiped progress.
      return { state: obj, dropped: false, reason: 'unknown_widget_kept' };
    }

    // Version skew check (only when the widget declared a version).
    if (widget.stateVersion !== undefined) {
      const persistedVersion = obj['__version'];
      if (typeof persistedVersion === 'number' && persistedVersion < widget.stateVersion) {
        return {
          state: undefined,
          dropped: true,
          reason: `version_skew (persisted=${persistedVersion} < current=${widget.stateVersion})`,
        };
      }
    }

    // Schema validation (only when the widget declared a schema).
    if (widget.stateSchema) {
      const result = widget.stateSchema.safeParse(persisted);
      if (!result.success) {
        const firstIssue = result.error.issues[0];
        const issuePath = firstIssue?.path?.join('.') ?? '';
        return {
          state: undefined,
          dropped: true,
          reason: `schema_invalid (${issuePath}: ${firstIssue?.message ?? 'unknown'})`,
        };
      }
      return { state: result.data, dropped: false };
    }

    return { state: obj, dropped: false };
  }
}

/**
 * Create a singleton widget registry instance
 */
export function createWidgetRegistry(): WidgetRegistry {
  return new WidgetRegistry();
}

/**
 * Default singleton instance
 */
export const widgetRegistry = new WidgetRegistry();
